import { ClickableItem, Flex, Link, TableColumn, TableRow, Text } from '@upsales/components';
import { ListViewDefaultColumn } from 'App/components/ListView/ListViewRenderHelpers';
import ListView, { ListViewPropsExternal } from 'App/components/ListView';
import openEditEsign from 'App/components/Esign/EditEsign/EditEsign';
import EsignAttributes from 'App/babel/attributes/EsignAttributes';
import InlineConfirm from 'Components/Dialogs/InlineConfirm';
import BemClass from '@upsales/components/Utils/bemClass';
import { Esign } from '../../../../resources/Model/Esign';
import RequestBuilder from 'Resources/RequestBuilder';
import openModal from 'App/services/Modal/Modal';
import logError from 'Helpers/logError';
import React from 'react';
import './ESigns.scss';

type Props = {
	clientId: number;
};

const ClientCardESigns = ({ clientId }: Props) => {
	const COLUMNS_ESIGN = ['mdate', 'filename', 'opportunity', 'status', 'signingParties', 'delete'];
	const classes = new BemClass('ClientCardESigns');

	const getEsign = async (rb: RequestBuilder, clientId: number) => {
		rb = rb ? rb.clone() : new RequestBuilder();
		rb.addFilter({ field: 'client.id' }, 'eq', clientId);
		return Tools.Esign.find(rb.build());
	};

	const isDraft = (esign: Esign) => esign.state === 0;
	const onEsignClick = (esign: Esign) => () => {
		const options = {
			esign: {
				client: {
					id: esign.client.id
				}
			},
			id: esign?.id
		};

		if (!esign || isDraft(esign)) {
			openEditEsign(options);
		} else {
			openModal('ConfirmEsignModal', {
				id: options.id
			});
		}
	};

	const onEsignDelete = async (esign: Esign): Promise<any> => {
		try {
			const result = await Tools.Esign.delete(esign);
			return result;
		} catch (err) {
			logError(err, 'Error deleting esign');
		}
	};

	const renderFilenameColumn = (esign: Esign) => {
		const URL = Tools.Esign.getDownloadUrl(esign);
		return (
			<Text size="sm">
				<Link target="_blank" href={URL}>
					{esign.title ? esign.title : esign.file?.filename || ''}
				</Link>
			</Text>
		);
	};

	const renderDeleteColumn = (esign: Esign) => {
		return esign.userRemovable && esign.state === 0 ? (
			<InlineConfirm show onConfirm={() => onEsignDelete(esign)}>
				<ClickableItem icon="trash" />
			</InlineConfirm>
		) : null;
	};

	const renderColumn = (esign: Esign, column: string, attributes: any) => {
		if (column === 'filename') {
			return (
				<TableColumn size="md" key={column}>
					<Flex>{renderFilenameColumn(esign)}</Flex>
				</TableColumn>
			);
		}

		if (column === 'delete') {
			return (
				<TableColumn size="md" key={column}>
					<Flex>{renderDeleteColumn(esign)}</Flex>
				</TableColumn>
			);
		}
		return <ListViewDefaultColumn<Esign> key={column} item={esign} column={column} attributes={attributes} />;
	};

	const getRowRenderFn = (): ListViewPropsExternal<Esign>['renderTableRow'] => {
		return (esign: Esign, { columns, attributes }) => {
			const columnElements = columns.map(column => renderColumn(esign, column, attributes));

			return (
				<TableRow key={esign.id} onClick={onEsignClick(esign)}>
					{columnElements}
				</TableRow>
			);
		};
	};

	return (
		<div className={classes.b()}>
			<ListView<Esign>
				noStickyTableHeader
				getData={(rb: RequestBuilder) => getEsign(rb, clientId)}
				columns={COLUMNS_ESIGN}
				attributes={EsignAttributes}
				renderTableRow={getRowRenderFn()}
				tableLimitOptions={[25]}
			/>
		</div>
	);
};

export default ClientCardESigns;
